import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  ArrowUpRight,
  CalendarDays,
  Check,
  Clock3,
  Lightbulb,
  LoaderCircle,
  Sparkles,
} from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { AutoLinkText } from "@/components/ui/auto-link-text";
import { Textarea } from "@/components/ui/textarea";
import { PodcastPlatformIcon } from "@/features/podcast/components/PodcastPlatformIcon";
import { EpisodeCommunity } from "@/features/podcast/community/EpisodeCommunity";
import { PODCAST_CONFIG } from "@/features/podcast/podcast-config";
import { podcastEpisodeSlug } from "@/features/podcast/podcast-rss";
import type { PodcastEpisode } from "@/features/podcast/types";
import { usePodcastFeed } from "@/features/podcast/usePodcastFeed";
import { SiteFooter } from "@/features/sales/storefront/components/SiteFooter";
import { SiteHeader } from "@/features/sales/storefront/components/SiteHeader";
import { trackEvent } from "@/lib/analytics";
import { absoluteUrl } from "@/lib/seo";

function getEpisode(slug: string) {
  return PODCAST_CONFIG.episodes.find((episode) => episode.slug === slug);
}

function formatDate(iso: string) {
  return new Intl.DateTimeFormat("es-CO", { dateStyle: "long" }).format(new Date(iso));
}

const DEFAULT_RELATED_RESOURCES = [
  {
    title: "Plan de Estudio Intensivo C1 — 30 días",
    description: "Convierte el aprendizaje en tareas diarias medibles.",
    href: "/productos/plan-estudio-intensivo-c1-30-dias",
  },
  {
    title: "Vocabulario Avanzado C1",
    description: "Practica familias de palabras, colocaciones y recuperación activa.",
    href: "/productos/vocabulario-avanzado-c1",
  },
  {
    title: "Guía Completa de Writing C1",
    description: "Trabaja con modelos, planificación y autoevaluación de escritura.",
    href: "/productos/guia-completa-writing-c1",
  },
];

export const Route = createFileRoute("/podcast_/$episodeSlug")({
  head: ({ params }) => {
    const episode = getEpisode(params.episodeSlug);
    const canonical = absoluteUrl(`/podcast/${params.episodeSlug}`);
    const title = episode ? `${episode.title} | Socrattica` : "Episodio | Socrattica";
    const description = episode?.description ?? "Escucha este episodio de Socrattica: El Podcast.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: canonical },
        { property: "og:image", content: absoluteUrl(PODCAST_CONFIG.coverImage) },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: episode
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "PodcastEpisode",
                name: episode.title,
                description: episode.description,
                url: canonical,
                datePublished: episode.publishedAt,
                timeRequired: episode.duration === "1:02" ? "PT1M2S" : undefined,
                transcript: episode.transcript?.join("\n\n"),
                partOfSeries: {
                  "@type": "PodcastSeries",
                  name: PODCAST_CONFIG.name,
                  url: absoluteUrl("/podcast"),
                },
                author: { "@type": "Person", name: PODCAST_CONFIG.author },
                inLanguage: "es-CO",
              }),
            },
          ]
        : [],
    };
  },
  component: PodcastEpisodePage,
});

function PodcastEpisodePage() {
  const { episodeSlug } = Route.useParams();
  const podcastFeed = usePodcastFeed();
  const baseEpisode =
    getEpisode(episodeSlug) ??
    podcastFeed.episodes.find((candidate) => podcastEpisodeSlug(candidate) === episodeSlug);
  const [generatedEditorial, setGeneratedEditorial] = useState<Partial<PodcastEpisode> | null>(
    null,
  );
  const episode = baseEpisode ? { ...baseEpisode, ...generatedEditorial } : undefined;

  useEffect(() => {
    if (!baseEpisode?.id) return;
    const controller = new AbortController();
    fetch(`/api/podcast-editorial?episodeId=${encodeURIComponent(baseEpisode.id)}`, {
      signal: controller.signal,
      headers: { Accept: "application/json" },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (data?.editorial) {
          setGeneratedEditorial({
            ...(data.editorial as Partial<PodcastEpisode>),
            description: data.editorial.summary ?? baseEpisode.description,
          });
        }
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, [baseEpisode?.description, baseEpisode?.id]);

  if (!episode && podcastFeed.status === "loading") {
    return (
      <div className="flex min-h-screen flex-col bg-white text-black">
        <SiteHeader />
        <main
          id="main-content"
          className="flex flex-1 items-center justify-center gap-3 px-6 py-24 text-sm"
          role="status"
        >
          <LoaderCircle className="h-5 w-5 animate-spin" />
          Preparando el laboratorio…
        </main>
        <SiteFooter />
      </div>
    );
  }

  if (!episode) {
    return (
      <div className="flex min-h-screen flex-col bg-white text-black">
        <SiteHeader />
        <main
          id="main-content"
          className="mx-auto flex w-full max-w-3xl flex-1 flex-col items-center justify-center px-6 py-24 text-center"
        >
          <h1 className="text-4xl font-semibold tracking-[-0.05em]">Episodio no encontrado.</h1>
          <Link to="/podcast" className="mt-6 underline underline-offset-4">
            Ver todos los episodios
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-white text-black">
      <SiteHeader />
      <main id="main-content" className="flex-1">
        <header className="border-b border-white/15 bg-black text-white">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 py-12 sm:px-6 sm:py-16 md:grid-cols-[minmax(0,1fr)_320px] md:items-center">
            <div>
              <Link
                to="/podcast"
                className="inline-flex items-center gap-2 text-xs font-semibold text-white/65 hover:text-white"
              >
                <ArrowLeft className="h-4 w-4" />
                Todos los episodios
              </Link>
              <p className="mt-10 text-xs font-semibold uppercase tracking-[0.16em] text-white/65">
                {episode.type ?? "Episodio"}
              </p>
              <h1 className="mt-3 max-w-4xl text-4xl font-semibold leading-[1.02] tracking-[-0.055em] sm:text-6xl">
                {episode.title}
              </h1>
              <p className="mt-5 max-w-2xl text-sm leading-7 text-white/65">
                <AutoLinkText text={episode.description} />
              </p>
              <div className="mt-6 flex flex-wrap gap-5 text-xs text-white/55">
                <span className="inline-flex items-center gap-2">
                  <CalendarDays className="h-4 w-4" />
                  {formatDate(episode.publishedAt)}
                </span>
                {episode.duration && (
                  <span className="inline-flex items-center gap-2">
                    <Clock3 className="h-4 w-4" />
                    {episode.duration}
                  </span>
                )}
              </div>
            </div>
            <img
              src={PODCAST_CONFIG.coverImage}
              alt={`Portada de ${PODCAST_CONFIG.name}`}
              className="aspect-square w-full border border-white/20 object-cover"
            />
          </div>
        </header>

        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-12 sm:px-6 sm:py-5 lg:grid-cols-[minmax(0,1fr)_320px]">
          <article className="min-w-0">
            <section aria-labelledby="episode-platforms-title">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-black/55">
                Apoya el pódcast
              </p>
              <h2
                id="episode-platforms-title"
                className="mt-2 text-2xl font-semibold tracking-[-0.04em]"
              >
                Elige dónde escucharlo
              </h2>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-black/60">
                La reproducción se realiza en las plataformas oficiales para que cada escucha apoye
                el crecimiento de Socrattica.
              </p>
              <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                {PODCAST_CONFIG.platforms.map((platform) => {
                  const platformUrl =
                    platform.kind === "spotify" ? episode.url || platform.url : platform.url;
                  if (!platformUrl) return null;

                  return (
                    <Button
                      key={platform.kind}
                      asChild
                      size="lg"
                      variant="outline"
                      className={
                        platform.kind === "apple"
                          ? "border-[#9933CC]/55 hover:border-[#9933CC] hover:bg-[#9933CC]/5 hover:text-black"
                          : platform.kind === "spotify"
                            ? "border-[#1DB954]/60 hover:border-[#1DB954] hover:bg-[#1DB954]/5 hover:text-black"
                            : "border-[#FF0000]/55 hover:border-[#FF0000] hover:bg-[#FF0000]/5 hover:text-black"
                      }
                    >
                      <a
                        href={platformUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() =>
                          void trackEvent("podcast_platform_click", {
                            episode_slug: episodeSlug,
                            episode_title: episode.title,
                            platform: platform.kind,
                          })
                        }
                      >
                        <PodcastPlatformIcon kind={platform.kind} />
                        Escuchar en {platform.name}
                        <ArrowUpRight className="h-4 w-4" />
                      </a>
                    </Button>
                  );
                })}
              </div>
            </section>

            <EpisodeLaboratory episode={episode} episodeSlug={episodeSlug} />

            {episode.transcript?.length ? (
              <section
                aria-labelledby="episode-transcript-title"
                className="mt-5 border-t border-black/15 pt-5"
              >
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-black/60">
                  Versión para lectura
                </p>
                <h2
                  id="episode-transcript-title"
                  className="mt-2 text-3xl font-semibold tracking-[-0.045em]"
                >
                  Transcripción
                </h2>
                <p className="mt-3 text-sm leading-6 text-black/60">
                  La puntuación fue editada para facilitar la lectura; puede omitir música o
                  saludos.
                </p>
                <div className="mt-7 space-y-5 text-base leading-8 text-black/75">
                  {episode.transcript.map((paragraph) => (
                    <p key={paragraph}>
                      <AutoLinkText text={paragraph} />
                    </p>
                  ))}
                </div>
              </section>
            ) : (
              <section
                aria-labelledby="episode-summary-title"
                className="mt-5 border-t border-black/15 pt-5"
              >
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-black/60">
                  Para volver a la idea central
                </p>
                <h2
                  id="episode-summary-title"
                  className="mt-2 text-3xl font-semibold tracking-[-0.045em]"
                >
                  Resumen del episodio
                </h2>
                <p className="mt-5 text-base leading-8 text-black/75">
                  <AutoLinkText text={episode.description} />
                </p>
              </section>
            )}

            {episode.references && episode.references.length > 0 && (
              <section
                aria-labelledby="episode-references-title"
                className="mt-5 border-t border-black/15 pt-5"
              >
                <h2
                  id="episode-references-title"
                  className="text-3xl font-semibold tracking-[-0.045em]"
                >
                  Referencias y lecturas
                </h2>
                <div className="mt-6 divide-y divide-black/10 border-y border-black/10">
                  {episode.references.map((reference) => (
                    <a
                      key={reference.url}
                      href={reference.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-start justify-between gap-5 py-6"
                    >
                      <span>
                        <span className="block text-sm font-semibold">{reference.title}</span>
                        {reference.authors && (
                          <span className="mt-1 block text-sm text-black/60">
                            {reference.authors}
                          </span>
                        )}
                        <span className="mt-2 block max-w-3xl text-sm leading-6 text-black/65">
                          {reference.description}
                        </span>
                      </span>
                      <ArrowUpRight className="h-4 w-4 shrink-0 text-black/45 group-hover:text-black" />
                    </a>
                  ))}
                </div>
              </section>
            )}
          </article>

          <RelatedEpisodeResources episode={episode} />
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

function EpisodeLaboratory({
  episode,
  episodeSlug,
}: {
  episode: PodcastEpisode;
  episodeSlug: string;
}) {
  const storageKey = `socrattica:episode-lab:${episodeSlug}`;
  const takeaways =
    episode.keyTakeaways?.length && episode.keyTakeaways.length > 0
      ? episode.keyTakeaways
      : [
          episode.description,
          "El progreso aparece cuando una idea se convierte en una práctica concreta y repetible.",
        ];
  const practice = episode.practice ?? {
    title: "Convierte una idea en una acción",
    duration: "5 minutos",
    steps: [
      "Escribe la idea del episodio que más cuestionó tu manera de estudiar.",
      "Conviértela en una práctica breve que puedas repetir esta semana.",
      "Define una evidencia observable para saber si la práctica funcionó.",
    ],
    reflectionPrompt:
      "Después de escuchar, ¿qué decisión concreta cambiarás durante los próximos siete días?",
  };
  const reflectionPrompt =
    practice.reflectionPrompt ??
    "¿Qué decisión concreta tomarás después de escuchar este episodio?";
  const [notes, setNotes] = useState("");

  useEffect(() => {
    try {
      setNotes(window.localStorage.getItem(storageKey) ?? "");
    } catch {
      // Las notas siguen disponibles durante la sesión si el navegador bloquea localStorage.
    }
  }, [storageKey]);

  return (
    <section aria-labelledby="episode-lab-title" className="mt-5 border-t border-black/15 pt-5">
      <div className="rounded-xl bg-black p-6 text-white sm:p-5">
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-white/60">
          <Sparkles className="h-4 w-4" />
          Laboratorio del episodio
        </p>
        <h2
          id="episode-lab-title"
          className="mt-3 text-3xl font-semibold tracking-[-0.045em] sm:text-4xl"
        >
          Escuchar es el comienzo. Ahora pruébalo.
        </h2>
        <p className="mt-3 max-w-2xl text-sm leading-6 text-white/65">
          Extrae lo esencial, haz una práctica corta y guarda una decisión que puedas comprobar.
        </p>
      </div>

      <div className="mt-5 grid gap-5 md:grid-cols-2">
        <div className="rounded-xl border border-black/15 p-5 sm:p-6">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-black/60">
            <Lightbulb className="h-4 w-4" />
            Ideas para llevarte
          </p>
          <ul className="mt-5 space-y-4">
            {takeaways.map((takeaway) => (
              <li key={takeaway} className="flex gap-3 text-sm leading-6 text-black/75">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-black text-white">
                  <Check className="h-3 w-3" />
                </span>
                <span>
                  <AutoLinkText text={takeaway} />
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-xl border border-black/15 p-5 sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-black/60">
                Práctica guiada
              </p>
              <h3 className="mt-2 text-xl font-semibold tracking-[-0.03em]">{practice.title}</h3>
            </div>
            {practice.duration && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-black px-3 py-1.5 text-xs font-semibold text-white">
                <Clock3 className="h-3.5 w-3.5" />
                {practice.duration}
              </span>
            )}
          </div>
          <ol className="mt-5 space-y-4">
            {practice.steps.map((step, index) => (
              <li key={step} className="flex gap-3 text-sm leading-6 text-black/75">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-black text-xs font-semibold">
                  {index + 1}
                </span>
                <span>
                  <AutoLinkText text={step} />
                </span>
              </li>
            ))}
          </ol>
        </div>
      </div>

      <div className="mt-5 rounded-xl border border-black/15 bg-black/[0.025] p-5 sm:p-6">
        <label htmlFor="episode-lab-notes" className="block">
          <span className="text-xs font-semibold uppercase tracking-[0.14em] text-black/60">
            Tu conclusión
          </span>
          <span className="mt-2 block text-xl font-semibold tracking-[-0.025em]">
            {reflectionPrompt}
          </span>
        </label>
        <Textarea
          id="episode-lab-notes"
          value={notes}
          onChange={(event) => {
            const nextNotes = event.target.value;
            setNotes(nextNotes);
            try {
              window.localStorage.setItem(storageKey, nextNotes);
            } catch {
              // La nota sigue disponible durante la sesión si el navegador bloquea localStorage.
            }
          }}
          maxLength={800}
          placeholder="Escribe una decisión que puedas observar y comprobar…"
          className="mt-4 min-h-32 resize-y border-black/20 bg-white text-base leading-6 md:text-base"
        />
        <EpisodeCommunity
          episodeSlug={episodeSlug}
          episodeTitle={episode.title}
          privateNotes={notes}
        />
      </div>
    </section>
  );
}

function RelatedEpisodeResources({ episode }: { episode: PodcastEpisode }) {
  const resources =
    episode.relatedResources?.length && episode.relatedResources.length > 0
      ? episode.relatedResources
      : DEFAULT_RELATED_RESOURCES;

  return (
    <aside className="h-fit rounded-xl border border-black/15 p-5 lg:sticky lg:top-24">
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-black/55">
        Continúa practicando
      </p>
      <h2 className="mt-2 text-xl font-semibold tracking-[-0.03em]">Recursos relacionados</h2>
      <div className="mt-5 divide-y divide-black/10 border-y border-black/10">
        {resources.map((resource) => (
          <a
            key={resource.href}
            href={resource.href}
            className="group flex items-start justify-between gap-3 py-5"
          >
            <span>
              <span className="block text-sm font-semibold leading-5">{resource.title}</span>
              <span className="mt-1 block text-sm leading-5 text-black/60">
                {resource.description}
              </span>
            </span>
            <ArrowUpRight className="mt-0.5 h-4 w-4 shrink-0 text-black/45 group-hover:text-black" />
          </a>
        ))}
      </div>
    </aside>
  );
}
